import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { LogOut, ChevronDown, WifiOff, MessageCircle, Check, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { SyncStatusIndicator } from '@/components/layout/SyncStatusIndicator';
import { NotificationInbox } from '@/components/layout/NotificationInbox';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { useChat } from '@/hooks/useChat';
import { MODULES, getActiveModule, MODULE_ACCENT } from '@/modules/index';
import type { Permission } from '@/lib/permissions';

// Legacy map — kept for direct lookups outside React (e.g. reports CSV).
// UI renders use t(`common:role.${role}`) so they stay locale-aware.
const ROLE_I18N_KEY = (role: string) => `common:role.${role}`;

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'maintenance', 'injection', 'quality']);
  const { workOrders } = useWorkOrders();
  const { user, logout } = useAuth();
  const { can } = usePermission();
  const { messages, isLoading, sendMessage } = useChat();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [justReconnected, setJustReconnected] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const prevMessageCountRef = useRef(messages.length);

  const activeModule = getActiveModule(location.pathname);
  const accent = MODULE_ACCENT[activeModule.id];

  const visibleModules = MODULES.filter(
    m => !m.permission || can(m.permission as Permission),
  );

  const visibleNavItems = activeModule.navItems.filter(
    item => !item.permission || can(item.permission as Permission),
  );

  useEffect(() => {
    const newCount = messages.length;
    if (!chatOpen && newCount > prevMessageCountRef.current) {
      const lastMessage = messages[newCount - 1];
      if (lastMessage?.role === 'assistant') setHasUnread(true);
    }
    prevMessageCountRef.current = newCount;
  }, [messages, chatOpen]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setJustReconnected(true);
      setTimeout(() => setJustReconnected(false), 3000);
    };
    const handleOffline = () => { setIsOnline(false); setJustReconnected(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const openWOCount = workOrders.filter(wo => wo.status !== 'closed').length;

  // Dashboard tab = exact match only; other tabs = prefix match
  const isActive = (href: string) =>
    href === activeModule.baseRoute
      ? location.pathname === href
      : location.pathname.startsWith(href);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-background">

      {/* Skip-to-content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded focus:shadow-md focus:text-sm"
      >
        Skip to main content
      </a>

      {/* ── Single-row header ── */}
      <header className="fixed top-0 left-0 right-0 z-40 h-14 bg-background/85 backdrop-blur-md border-b border-border/70 flex items-center gap-0 px-4">

        {/* Hot runner — molten brand hairline */}
        <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] hot-runner" />

        {/* Brand */}
        <Link to={activeModule.baseRoute} className="flex items-center flex-shrink-0 min-h-[44px] py-1 mr-2">
          <img src="/logo.png" alt="MoldOp" className="h-7 object-contain mix-blend-multiply dark:mix-blend-normal" />
        </Link>

        {/* Module switcher dropdown */}
        {visibleModules.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'flex items-center gap-1.5 h-8 px-2.5 text-[13px] font-medium rounded-md border mr-3 flex-shrink-0',
                  accent.pill,
                )}
              >
                <span>{t(activeModule.labelKey)}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {visibleModules.map(mod => (
                <DropdownMenuItem
                  key={mod.id}
                  onClick={() => navigate(mod.baseRoute)}
                  className="flex items-center gap-2 text-[13px] cursor-pointer"
                >
                  <span>{t(mod.labelKey)}</span>
                  {activeModule.id === mod.id && (
                    <Check className="h-3.5 w-3.5 ml-auto text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Nav tabs — horizontally scrollable on small screens */}
        <nav
          className="flex-1 overflow-x-auto min-w-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Section navigation"
        >
          <ul className="flex items-center gap-0.5 min-w-max">
            {visibleNavItems.map(item => {
              const active = isActive(item.href);
              const badgeCount = item.badge ? openWOCount : 0;
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'group relative flex items-center gap-1.5 px-3 h-14 min-h-[44px] text-[13px] font-medium transition-colors whitespace-nowrap',
                      active
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {active && (
                      <span className="absolute bottom-0 left-3 right-3 h-[2.5px] rounded-full hot-runner" />
                    )}
                    <item.icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.6} />
                    <span>{t(item.labelKey)}</span>
                    {badgeCount > 0 && (
                      <span className={cn(
                        'rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                        active
                          ? 'bg-molten/10 text-molten-deep'
                          : 'bg-muted text-muted-foreground',
                      )}>
                        {badgeCount}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right side: sync + chat + user */}
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">

          <SyncStatusIndicator collapsed={true} />

          <LanguageToggle compact />

          <NotificationInbox />

          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8"
            onClick={() => { setChatOpen(prev => !prev); setHasUnread(false); }}
            aria-label="Open MoldOp Chatbot"
          >
            <MessageCircle className="h-4 w-4" />
            {hasUnread && (
              <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-destructive" />
            )}
          </Button>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 h-9 px-2.5 text-[13px] font-normal text-muted-foreground hover:text-foreground"
                >
                  <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-[11px] font-semibold text-primary-foreground">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:inline">{user.name}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-[13px] font-medium text-foreground">{user.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {t(ROLE_I18N_KEY(user.role), { defaultValue: user.role })}
                  </p>
                </div>
                <DropdownMenuSeparator />
                {(() => {
                  const gate: Record<string, Permission | undefined> = {
                    maintenance: undefined,
                    inject: 'inject:manage' as Permission,
                    quality: 'quality:manage' as Permission,
                  };
                  const perm = gate[activeModule.id];
                  if (perm && !can(perm)) return null;
                  return (
                    <DropdownMenuItem
                      onClick={() => navigate(`${activeModule.baseRoute}/settings`)}
                      className="text-[13px] gap-2 cursor-pointer"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      {t('common:nav.settings')}
                    </DropdownMenuItem>
                  );
                })()}
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-[13px] gap-2 cursor-pointer"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {t('common:nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>

      {/* ── Offline / reconnected banner ── */}
      {!isOnline && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-14 left-0 right-0 z-30 flex items-center justify-center gap-2 status-amber border-b text-sm py-2 px-4"
        >
          <WifiOff className="h-4 w-4 shrink-0" />
          You're offline — changes will sync when connection returns
        </div>
      )}
      {justReconnected && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-14 left-0 right-0 z-30 flex items-center justify-center gap-2 status-green border-b text-sm py-2 px-4"
        >
          ✓ Back online — syncing changes
        </div>
      )}

      {/* ── Page content ── */}
      <main id="main-content" className={cn('flex-1 pt-14', !isOnline && 'pt-[3.5rem]')}>
        <div key={location.pathname} className="animate-page-enter">
          {children}
        </div>
      </main>

      {/* ── Chat panel ── */}
      <ChatWidget
        isOpen={chatOpen}
        messages={messages}
        isLoading={isLoading}
        onSend={sendMessage}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
}
